import type { User } from '../types/index';

/**
 * Utility functions for user type detection and routing
 */

export const getUserDashboardPath = (userRole: 'admin' | 'superadmin' | null): string => {
  switch (userRole) {
    case 'admin':
    case 'superadmin':
      return '/admin/dashboard';
    default:
      return '/';
  }
};

export const getDefaultRedirectPath = (user: User | null): string => {
  if (!user) return '/';
  
  return getUserDashboardPath(user.role);
};

export const isAdminUser = (user: User | null): boolean => {
  return user?.role === 'admin' || user?.role === 'superadmin';
};

export const canAccessAdminFeatures = (user: User | null): boolean => {
  return isAdminUser(user);
};

export const canAccessUserFeatures = (user: User | null): boolean => {
  return isAdminUser(user);
};

export const getAvailableRoutes = (userRole: 'admin' | 'superadmin' | null): string[] => {
  const baseRoutes = ['/'];
  
  switch (userRole) {
    case 'admin':
    case 'superadmin':
      return [
        ...baseRoutes,
        '/admin/dashboard',
        '/admin/users',
        '/admin/test',
        '/admin/analytics'
      ];
    default:
      return baseRoutes;
  }
};

/**
 * Generate a unique guest session ID
 */
export const generateGuestSessionId = (): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `guest-${timestamp}-${random}`;
};

/**
 * Check if a guest session is still valid
 */
export const isGuestSessionValid = (expiresAt: Date): boolean => {
  return new Date() < new Date(expiresAt);
};

/**
 * Get guest session expiry time (24 hours from now)
 */
export const getGuestSessionExpiry = (): Date => {
  const now = new Date();
  return new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours
};
